<script lang="ts">
	import Button from '$lib/components/primitives/Button.svelte';
	import { _ } from 'svelte-i18n';
	
	let {
		title = undefined,
		subtitle = undefined,
		primaryCTA = undefined,
		primaryHref = 'https://app.heyweek.com/?signup',
		features = undefined,
		showPricingLink = true,
		backgroundVariant = 'gradient' as 'gradient' | 'pattern' | 'solid'
	} = $props();
	
	// Make translations reactive using $derived
	const displayTitle = $derived(title ?? $_('finalCTA.title'));
	const displaySubtitle = $derived(subtitle ?? $_('finalCTA.subtitle'));
	const displayPrimaryCTA = $derived(primaryCTA ?? $_('finalCTA.primaryButton'));
	const displayFeatures = $derived(features ?? [
		$_('finalCTA.features.feature1'),
		$_('finalCTA.features.feature2'),
		$_('finalCTA.features.feature3'),
		$_('finalCTA.features.feature4')
	]);
</script>

<section class="final-cta final-cta--{backgroundVariant}">
	<div class="container">
		<div class="cta__content">
			<h2 class="cta__title">{displayTitle}</h2>
			<p class="cta__subtitle">{displaySubtitle}</p>
			
			<div class="cta__buttons">
				<Button 
					variant="primary" 
					size="lg" 
					href={primaryHref}
					class="cta__primary"
				>
					{displayPrimaryCTA}
					<svg class="button__arrow" viewBox="0 0 20 20" fill="currentColor">
						<path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
					</svg>
				</Button>
			</div>
			
			<div class="cta__features">
				{#each displayFeatures as feature}
					<div class="feature">
						<svg class="feature__icon" viewBox="0 0 20 20" fill="currentColor">
							<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
						</svg>
						<span>{feature}</span>
					</div>
				{/each}
			</div>
			
			{#if showPricingLink}
				<div class="cta__pricing-link">
					<a href="/pricing" class="pricing-link">
						{$_('pricing.faqLinks.pricing')} →
					</a>
				</div>
			{/if}
		</div>
		
		<!-- Decorative elements -->
		<div class="cta__decoration cta__decoration--left" aria-hidden="true">
			<svg viewBox="0 0 200 200" class="decoration__svg">
				<circle cx="100" cy="100" r="80" fill="currentColor" opacity="0.1" />
				<circle cx="100" cy="100" r="60" fill="currentColor" opacity="0.1" />
				<circle cx="100" cy="100" r="40" fill="currentColor" opacity="0.1" />
			</svg>
		</div>
		<div class="cta__decoration cta__decoration--right" aria-hidden="true">
			<svg viewBox="0 0 200 200" class="decoration__svg">
				<rect x="40" y="40" width="120" height="120" fill="currentColor" opacity="0.1" transform="rotate(45 100 100)" />
				<rect x="60" y="60" width="80" height="80" fill="currentColor" opacity="0.1" transform="rotate(45 100 100)" />
			</svg>
		</div>
	</div>
</section>

<style>
	.final-cta {
		padding-block: var(--section-padding-y);
		position: relative;
		overflow: hidden;
	}
	
	/* Background variants */
	.final-cta--gradient {
		background: linear-gradient(
			135deg,
			var(--bg-secondary),
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.05)
		);
		color: var(--text-primary);
	}
	
	.final-cta--gradient .cta__title,
	.final-cta--gradient .cta__subtitle,
	.final-cta--gradient .feature {
		color: var(--text-primary);
	}
	
	
	.final-cta--pattern {
		background: var(--bg-secondary);
		background-image: 
			radial-gradient(circle at 20% 50%, hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.05) 0%, transparent 50%),
			radial-gradient(circle at 80% 50%, hsl(var(--secondary-h) var(--secondary-s) var(--secondary-l) / 0.05) 0%, transparent 50%);
	}
	
	.final-cta--solid {
		background: var(--bg-secondary);
		border-top: 1px solid var(--border-light);
		border-bottom: 1px solid var(--border-light);
	}
	
	.container {
		max-width: var(--container-max);
		margin-inline: auto;
		padding-inline: var(--container-padding);
		position: relative;
	}
	
	.cta__content {
		max-width: 800px;
		margin-inline: auto;
		text-align: center;
		position: relative;
		z-index: 1;
	}
	
	.cta__title {
		font-size: clamp(var(--font-size-3xl), 5vw, var(--font-size-5xl));
		font-weight: var(--font-weight-bold);
		margin-bottom: var(--space-4);
		line-height: var(--line-height-tight);
		animation: fadeInUp 0.6s ease-out;
	}
	
	.final-cta--gradient .cta__title,
	.final-cta--pattern .cta__title,
	.final-cta--solid .cta__title {
		background: linear-gradient(135deg, var(--text-primary) 0%, var(--text-brand) 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
	}
	
	.cta__subtitle {
		font-size: var(--font-size-lg);
		line-height: var(--line-height-relaxed);
		margin-bottom: var(--space-8);
		opacity: 0.9;
		animation: fadeInUp 0.6s ease-out 0.1s backwards;
	}
	
	.final-cta--gradient .cta__subtitle,
	.final-cta--pattern .cta__subtitle,
	.final-cta--solid .cta__subtitle {
		color: var(--text-secondary);
	}
	
	.cta__buttons {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: var(--space-8);
		animation: fadeInUp 0.6s ease-out 0.2s backwards;
	}
	
	:global(.cta__primary) {
		padding-inline: var(--space-8) !important;
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		position: relative;
		overflow: hidden;
	}
	
	.final-cta--gradient :global(.cta__primary) {
		background: var(--brand-primary);
		color: white;
	}
	
	.final-cta--gradient :global(.cta__primary:hover) {
		background: hsl(var(--brand-h) var(--brand-s) calc(var(--brand-l) - 5%));
		transform: translateY(-2px);
		box-shadow: 0 10px 30px hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.3);
	}
	
	.button__arrow {
		width: 20px;
		height: 20px;
		transition: transform 0.3s ease;
	}
	
	:global(.cta__primary:hover) .button__arrow {
		transform: translateX(4px);
	}
	
	
	.cta__features {
		display: flex;
		justify-content: center;
		flex-wrap: wrap;
		gap: var(--space-4) var(--space-6);
		animation: fadeInUp 0.6s ease-out 0.3s backwards;
	}
	
	.feature {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--font-size-sm);
		opacity: 0.9;
	}
	
	.final-cta--gradient .feature,
	.final-cta--pattern .feature,
	.final-cta--solid .feature {
		color: var(--text-secondary);
	}
	
	.feature__icon {
		width: 20px;
		height: 20px;
		color: currentColor;
		opacity: 0.7;
	}
	
	/* Pricing link */
	.cta__pricing-link {
		margin-top: var(--space-6);
		animation: fadeInUp 0.6s ease-out 0.4s backwards;
	}
	
	.pricing-link {
		color: inherit;
		opacity: 0.8;
		text-decoration: underline;
		text-underline-offset: var(--text-underline-offset);
		font-size: var(--font-size-base);
		transition: var(--transition-all);
	}
	
	.pricing-link:hover {
		opacity: 1;
		transform: translateX(4px);
	}
	
	.final-cta--gradient .pricing-link {
		color: var(--text-brand);
	}
	
	.final-cta--pattern .pricing-link,
	.final-cta--solid .pricing-link {
		color: var(--text-brand);
	}
	
	/* Decorative elements */
	.cta__decoration {
		position: absolute;
		pointer-events: none;
		z-index: 0;
	}
	
	.cta__decoration--left {
		top: 50%;
		left: -100px;
		transform: translateY(-50%);
		color: currentColor;
		animation: float 20s ease-in-out infinite;
	}
	
	.cta__decoration--right {
		top: 50%;
		right: -100px;
		transform: translateY(-50%);
		color: currentColor;
		animation: float 20s ease-in-out infinite reverse;
	}
	
	.decoration__svg {
		width: 300px;
		height: 300px;
	}
	
	.final-cta--gradient .cta__decoration {
		color: var(--brand-primary);
	}
	
	.final-cta--pattern .cta__decoration,
	.final-cta--solid .cta__decoration {
		color: var(--brand-primary);
	}
	
	/* Animations */
	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
	
	@keyframes float {
		0%, 100% {
			transform: translateY(-50%) rotate(0deg);
		}
		25% {
			transform: translateY(-55%) rotate(5deg);
		}
		50% {
			transform: translateY(-45%) rotate(-5deg);
		}
		75% {
			transform: translateY(-55%) rotate(5deg);
		}
	}
	
	/* Responsive */
	@media (max-width: 768px) {
		.cta__buttons {
			flex-direction: column;
			align-items: center;
		}
		
		:global(.cta__primary) {
			width: 100%;
			max-width: 300px;
			justify-content: center;
		}
		
		.cta__features {
			flex-direction: column;
			align-items: center;
		}
		
		.cta__decoration {
			display: none;
		}
	}
</style>